/**
 * FinProTemplate — Finance / CA / CFA / Accounting / Banking
 *
 * ATS-OPTIMISED TWO-COLUMN LAYOUT:
 *   DOM order:  Name → Contact → Summary → Experience → Internships →
 *               Projects → Achievements → Certifications →
 *               Education → Skills → Languages → Interests
 *   Visual:     LEFT sidebar (~29%) + RIGHT main (~71%) via CSS Grid areas
 *
 * ATS parsers read the HTML in DOM order — so they get the most important
 * content first, while humans still see the beautiful two-column layout.
 */
import { normalizeTemplateData, safePoint, ResumeLink } from './templateHelpers';

const FF = "'Inter','Segoe UI',Helvetica,Arial,sans-serif";

/* ── hex → "r,g,b" ────────────────────────────────────────────────────── */
function hexToRgb(hex) {
    if (!hex || hex.length < 7) return '140,98,57';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
}

/* ── Left section heading ──────────────────────────────────────────────── */
function LSH({ title, a, fs }) {
    return (
        <div style={{ marginBottom: 5 }}>
            <h2 style={{ fontSize: fs(8.5), fontWeight: 800, color: a, textTransform: 'uppercase', letterSpacing: '1.1px', margin: '0 0 2px', fontFamily: FF }}>{title}</h2>
            <div style={{ height: 1.5, background: a, opacity: 0.4 }} />
        </div>
    );
}

/* ── Right section heading ─────────────────────────────────────────────── */
function RSH({ title, a, fs }) {
    return (
        <div style={{ marginBottom: 5 }}>
            <h2 style={{ fontSize: fs(9.5), fontWeight: 800, color: a, textTransform: 'uppercase', letterSpacing: '1.2px', margin: '0 0 2px', fontFamily: FF }}>{title}</h2>
            <div style={{ height: 1.5, background: a, opacity: 0.4 }} />
        </div>
    );
}

/* ── Bullet — ATS-friendly standard disc markers ───────────────────────── */
function Bullet({ text, fs }) {
    return (
        <li style={{ marginLeft: 12, marginBottom: 1.5, fontSize: fs(8.5), color: '#2d2d2d', lineHeight: 1.45, listStyleType: 'disc', listStylePosition: 'outside', paddingLeft: 2 }}>
            {text}
        </li>
    );
}

/* ── Skills auto-categorization ────────────────────────────────────────── */
const SKILL_HEADERS = new Set([
    'technical skills', 'soft skills', 'other skills', 'core competencies',
    'key skills', 'professional skills', 'domain skills', 'hard skills',
    'analytical skills', 'management skills', 'computer skills',
]);

function parseSkillCategories(skills) {
    // Strip sub-category header strings the AI sometimes injects
    const clean = skills.filter(s => {
        if (typeof s !== 'string') return false;
        const norm = s.toLowerCase().trim().replace(/:$/, '');
        return !SKILL_HEADERS.has(norm);
    });

    const tech      = clean.filter(s => /excel|power ?bi|tally|sap|quickbooks|sql|python|tableau|erp|vlookup|pivot|powerpoint|word|ms ?office|microsoft office|microsoft excel|zoho|hrms|hris|darwinbox|workday/i.test(s));
    const analytics = clean.filter(s => /analy|report|qbr|mbr|dashboard|forecast|modell?ing|valuation|dcf|lbo|mis/i.test(s));
    const finance   = clean.filter(s => /gst|tds|tax|audit|ifrs|ind ?as|budget|invoice|account|banking|treasury|fema|rbi|sebi|icai/i.test(s));
    const soft      = clean.filter(s => /communicat|leadership|teamwork|adaptab|accuracy|learning|problem|manag|time|attention|collaborat/i.test(s));

    const assigned = new Set([...tech, ...analytics, ...finance, ...soft].map(s => s.toLowerCase()));
    const other    = clean.filter(s => !assigned.has(s.toLowerCase()));

    const cats = [];
    if (tech.length)      cats.push({ label: 'Technical Skills',       items: tech });
    if (analytics.length) cats.push({ label: 'Analytical & Reporting', items: analytics });
    if (finance.length)   cats.push({ label: 'Finance & Business',     items: finance });
    if (soft.length)      cats.push({ label: 'Soft Skills',            items: soft });
    if (other.length)     cats.push({ label: 'Other Skills',           items: other });
    if (cats.length === 0 && clean.length > 0) cats.push({ label: 'Skills', items: clean });
    return cats;
}

/* ── Main Template ─────────────────────────────────────────────────────── */
function FinProTemplate({ resumeData, accentColor = '#8c6239', fontScale = 1, editMode = false, onFieldUpdate = () => {} }) {
    if (!resumeData) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 60, flexDirection: 'column', textAlign: 'center' }}>
                <p style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>Chat with AI to build your Finance resume</p>
                <p style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>e.g. "I'm a CA with articleship at a Big 4 firm"</p>
            </div>
        );
    }

    const d  = normalizeTemplateData(resumeData);
    const a  = accentColor;
    const fs = (base) => `${(base * fontScale).toFixed(1)}px`;

    let rgb = '140,98,57';
    try { rgb = hexToRgb(a); } catch (_) {}
    const sidebarBg = `rgba(${rgb}, 0.07)`;

    // Prefer user-defined categories from form editor; fall back to finance auto-categorisation
    const skillCats = (Array.isArray(d.skillCategories) && d.skillCategories.length > 0)
        ? d.skillCategories
            .filter(c => Array.isArray(c.skills) && c.skills.length > 0)
            .map(c => ({ label: c.name, items: c.skills }))
        : parseSkillCategories(d.skills);

    const stripUrl  = u => u ? u.replace(/^https?:\/\/(www\.)?/, '') : '';

    const interestKeywords = /gym|yoga|read|book|travel|music|art|cook|sport|cricket|football|chess|photography|nature|history|culture|movie|fitness|gaming|dance|singing|trek|blog|hobby|interest|passion/i;
    const extraRaw         = d.extraList || [];
    const interestsRaw     = (d.interests && d.interests.length > 0) ? d.interests
        : (d.hobbies && d.hobbies.length > 0) ? d.hobbies
        : extraRaw.filter(e => interestKeywords.test(e) && e.length < 60);
    const extraCurriculars = extraRaw.filter(e => !interestsRaw.includes(e));

    const membershipPattern = /\bicai\b|\bicsi\b|\bicmai\b|\bicwai\b|\bfellow\b|membership\s*no|registration\s*no/i;
    const memberships   = extraCurriculars.filter(e =>  membershipPattern.test(e));
    const extraFiltered = extraCurriculars.filter(e => !membershipPattern.test(e));

    const secMB = 11;

    /*
     /*
      * ATS DOM ORDER: Name first in DOM, placed in right column via CSS grid.
      * Sidebar is second in DOM but visually on the LEFT via grid-column: 1.
      * ATS reads: Name → Contact → Summary → Experience → Education → Skills ✅
      * Human sees: Left sidebar | Right column with name at top ✅
      */

    return (
        <div id="resume" style={{
            fontFamily: FF,
            background: '#fff',
            color: '#1a1a1a',
            fontSize: fs(9),
            lineHeight: 1.45,
            display: 'grid',
            gridTemplateColumns: '29% 71%',
        }}>

            {/* ══ NAME + ROLE — DOM position 1, placed in RIGHT column via CSS grid ══ */}
            <div style={{
                gridColumn: '2',
                gridRow: '1',
                padding: '14px 16px 10px 16px',
                borderBottom: `2px solid rgba(${rgb},0.12)`,
                background: '#fff',
            }}>
                {d.name ? (
                    d.name.trim().split(' ').length >= 3 ? (
                        <h1 style={{ fontSize: fs(21), fontWeight: 900, color: '#1a1a1a', margin: 0, lineHeight: 1.05, letterSpacing: '-0.2px', fontFamily: FF, textTransform: 'uppercase' }}>
                            {d.name.trim().split(' ').slice(0, Math.ceil(d.name.trim().split(' ').length / 2)).join(' ')}{' '}
                            {d.name.trim().split(' ').slice(Math.ceil(d.name.trim().split(' ').length / 2)).join(' ')}
                        </h1>
                    ) : (
                        <h1 style={{ fontSize: fs(21), fontWeight: 900, color: '#1a1a1a', margin: 0, lineHeight: 1.05, fontFamily: FF, textTransform: 'uppercase' }}>{d.name}</h1>
                    )
                ) : (
                    <h1 style={{ fontSize: fs(21), fontWeight: 900, color: '#1a1a1a', margin: 0, lineHeight: 1.05, fontFamily: FF, textTransform: 'uppercase' }}>YOUR NAME</h1>
                )}
                {d.role && (
                    <div style={{ fontSize: fs(9), fontWeight: 600, color: a, marginTop: 4, letterSpacing: '0.2px' }}>{d.role}</div>
                )}
            </div>

            {/* ══ SIDEBAR — DOM position 2, placed in LEFT column via CSS grid ══ */}
            {/* ATS reads this after name, before main content ✅ */}
            <div style={{
                gridColumn: '1',
                gridRow: '1 / 999',
                background: sidebarBg,
                padding: '14px 11px 18px 12px',
                boxSizing: 'border-box',
                borderRight: `2px solid rgba(${rgb},0.12)`,
            }}>

                {/* CONTACT — always first in sidebar, not part of section_order */}
                <div style={{ marginBottom: secMB }}>
                    <LSH title="Contact" a={a} fs={fs} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                        {d.city  && <div style={{ fontSize: fs(8.5), color: '#333', lineHeight: 1.4 }}>{d.city}</div>}
                        {d.phone && <div style={{ fontSize: fs(8.5), color: '#333' }}>{d.phone}</div>}
                        {d.email && <div style={{ fontSize: fs(8.5), color: '#333', wordBreak: 'break-all' }}>{d.email}</div>}
                        {d.links?.linkedin  && <div><ResumeLink href={d.links.linkedin}  label={stripUrl(d.links.linkedin)}  style={{ fontSize: fs(8.5), color: a }} /></div>}
                        {d.links?.portfolio && <div><ResumeLink href={d.links.portfolio} label={stripUrl(d.links.portfolio)} style={{ fontSize: fs(8.5), color: a }} /></div>}
                    </div>
                </div>

                {/* ── Dynamic sidebar section ordering via SIDEBAR_RENDERERS ── */}
                {(() => {
                    const SIDEBAR_RENDERERS = {
                        education: () => d.educationList.length > 0 ? (
                            <div key="education" style={{ marginBottom: secMB }}>
                                <LSH title="Education" a={a} fs={fs} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                    {d.educationList.map((edu, i) => (
                                        <div key={i}>
                                            <div style={{ fontSize: fs(9), fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>{edu?.college}</div>
                                            <ul style={{ margin: '1px 0 0', padding: 0 }}>
                                                <Bullet fs={fs} text={`${edu?.degree || ''}${edu?.year ? ` (${edu.year})` : ''}${edu?.cgpa ? ` — ${edu.cgpa}` : ''}`} />
                                                {edu?.specialization && <Bullet fs={fs} text={`Specialization: ${edu.specialization}`} />}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null,

                        skills: () => skillCats.length > 0 ? (
                            <div key="skills" style={{ marginBottom: secMB }}>
                                <LSH title="Skills" a={a} fs={fs} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                    {skillCats.map((cat, i) => (
                                        <div key={i}>
                                            <div style={{ fontSize: fs(8.5), fontWeight: 700, color: '#1a1a1a', marginBottom: 1 }}>{cat.label}:</div>
                                            <ul style={{ margin: 0, padding: 0 }}>
                                                {cat.items.map((skill, j) => <Bullet key={j} fs={fs} text={skill} />)}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null,

                        languages: () => d.languageList.length > 0 ? (
                            <div key="languages" style={{ marginBottom: secMB }}>
                                <LSH title="Language" a={a} fs={fs} />
                                <ul style={{ margin: 0, padding: 0 }}>
                                    {d.languageList.map((lang, i) => <Bullet key={i} fs={fs} text={lang} />)}
                                </ul>
                            </div>
                        ) : null,

                        interests: () => interestsRaw.length > 0 ? (
                            <div key="interests" style={{ marginBottom: secMB }}>
                                <LSH title="Areas of Interest" a={a} fs={fs} />
                                <ul style={{ margin: 0, padding: 0 }}>
                                    {interestsRaw.map((item, i) => <Bullet key={i} fs={fs} text={item} />)}
                                </ul>
                            </div>
                        ) : null,

                        memberships: () => memberships.length > 0 ? (
                            <div key="memberships">
                                <LSH title="Memberships" a={a} fs={fs} />
                                <ul style={{ margin: 0, padding: 0 }}>
                                    {memberships.map((item, i) => <Bullet key={i} fs={fs} text={item} />)}
                                </ul>
                            </div>
                        ) : null,
                    };

                    const SIDEBAR_DEFAULT_ORDER = ['education', 'skills', 'languages', 'interests', 'memberships'];
                    const savedOrder = Array.isArray(resumeData.section_order) && resumeData.section_order.length > 0
                        ? resumeData.section_order
                        : SIDEBAR_DEFAULT_ORDER;
                    const orderedKeys = [
                        ...savedOrder.filter(k => SIDEBAR_RENDERERS[k]),
                        ...SIDEBAR_DEFAULT_ORDER.filter(k => SIDEBAR_RENDERERS[k] && !savedOrder.includes(k)),
                    ];

                    return orderedKeys.map(key => SIDEBAR_RENDERERS[key] ? SIDEBAR_RENDERERS[key]() : null);
                })()}
            </div>

            {/* ══ MAIN CONTENT — DOM position 3, placed in RIGHT column row 2 ══ */}
            {/* ATS reads: name → sidebar (contact/edu/skills) → summary → experience ✅ */}
            <div style={{ gridColumn: '2', gridRow: '2', padding: '0 16px 18px 16px', boxSizing: 'border-box' }}>

                {/* ── Dynamic main section ordering via MAIN_RENDERERS ── */}
                {(() => {
                    const MAIN_RENDERERS = {
                        summary: () => d.summaryText ? (
                            <div key="summary" style={{ marginBottom: secMB }}>
                                <RSH title="Professional Summary" a={a} fs={fs} />
                                <p style={{ fontSize: fs(8.5), color: '#333', lineHeight: 1.5, margin: 0 }}>{d.summaryText}</p>
                            </div>
                        ) : null,

                        experience: () => d.experience.length > 0 ? (
                            <div key="experience" style={{ marginBottom: secMB }}>
                                <RSH title="Work Experience" a={a} fs={fs} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                                    {d.experience.map((exp, i) => (
                                        <div key={i}>
                                            <div style={{ fontSize: fs(9.5), fontWeight: 700, color: '#1a1a1a', lineHeight: 1.25 }}>{exp.role}</div>
                                            {exp.company  && <div style={{ fontSize: fs(8.5), color: '#555', marginTop: 1 }}>{exp.company}</div>}
                                            {exp.duration && <div style={{ fontSize: fs(8), color: '#888', marginTop: 1, marginBottom: 2 }}>{exp.duration}{exp.location ? ` · ${exp.location}` : ''}</div>}
                                            {exp.points?.length > 0 && (
                                                <ul style={{ margin: '1px 0 0', padding: 0 }}>
                                                    {exp.points.map((pt, j) => <Bullet key={j} fs={fs} text={safePoint(pt)} />)}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null,

                        projects: () => d.projects.length > 0 ? (
                            <div key="projects" style={{ marginBottom: secMB }}>
                                <RSH title="Key Projects & Engagements" a={a} fs={fs} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                    {d.projects.map((proj, i) => (
                                        <div key={i}>
                                            <div style={{ fontSize: fs(9), fontWeight: 700, color: '#1a1a1a' }}>{proj.title || proj.name}</div>
                                            {proj.description && <ul style={{ margin: '1px 0 0', padding: 0 }}><Bullet fs={fs} text={proj.description} /></ul>}
                                            {proj.points?.length > 0 && (
                                                <ul style={{ margin: '1px 0 0', padding: 0 }}>
                                                    {proj.points.map((pt, j) => <Bullet key={j} fs={fs} text={safePoint(pt)} />)}
                                                </ul>
                                            )}
                                            {proj.impact && <div style={{ fontSize: fs(8), color: '#555', fontWeight: 600, marginTop: 1 }}>Impact: {proj.impact}</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null,

                        achievements: () => d.achieveList.length > 0 ? (
                            <div key="achievements" style={{ marginBottom: secMB }}>
                                <RSH title="Achievement" a={a} fs={fs} />
                                <ul style={{ margin: 0, padding: 0 }}>
                                    {d.achieveList.map((item, i) => <Bullet key={i} fs={fs} text={item} />)}
                                </ul>
                            </div>
                        ) : null,

                        certifications: () => d.certList.length > 0 ? (
                            <div key="certifications" style={{ marginBottom: secMB }}>
                                <RSH title="Certifications" a={a} fs={fs} />
                                <ul style={{ margin: 0, padding: 0 }}>
                                    {d.certList.map((cert, i) => <Bullet key={i} fs={fs} text={cert} />)}
                                </ul>
                            </div>
                        ) : null,

                        extracurricular: () => extraFiltered.length > 0 ? (
                            <div key="extracurricular">
                                <RSH title="Extra Curriculars" a={a} fs={fs} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                    {extraFiltered.map((item, i) => {
                                        const colonIdx = item.indexOf(':');
                                        if (colonIdx > 0 && colonIdx < 60) {
                                            const heading = item.slice(0, colonIdx).trim();
                                            const desc    = item.slice(colonIdx + 1).trim();
                                            return (
                                                <div key={i}>
                                                    <div style={{ fontSize: fs(9), fontWeight: 700, color: '#1a1a1a', marginBottom: 1 }}>{heading}:</div>
                                                    {desc && <ul style={{ margin: 0, padding: 0 }}><Bullet fs={fs} text={desc} /></ul>}
                                                </div>
                                            );
                                        }
                                        return <ul key={i} style={{ margin: 0, padding: 0 }}><Bullet fs={fs} text={item} /></ul>;
                                    })}
                                </div>
                            </div>
                        ) : null,
                    };

                    const MAIN_DEFAULT_ORDER = ['summary', 'experience', 'projects', 'achievements', 'certifications', 'extracurricular'];
                    const savedOrder = Array.isArray(resumeData.section_order) && resumeData.section_order.length > 0
                        ? resumeData.section_order
                        : MAIN_DEFAULT_ORDER;
                    const orderedKeys = [
                        ...savedOrder.filter(k => MAIN_RENDERERS[k]),
                        ...MAIN_DEFAULT_ORDER.filter(k => MAIN_RENDERERS[k] && !savedOrder.includes(k)),
                    ];

                    return orderedKeys.map(key => MAIN_RENDERERS[key] ? MAIN_RENDERERS[key]() : null);
                })()}

            </div>
        </div>
    );
}

export default FinProTemplate;
